import { useContext, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { BLOCKS } from '@contentful/rich-text-types'
import { fromZonedTime } from 'date-fns-tz'
import { createSerializer } from 'nuqs'

import type { ISectionBookingBarHeroFields } from 'contentful-shared'
import { ContentfulImage } from 'bl-common/src/elements/ContentfulImage'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { RichTextRenderer } from 'bl-common/src/richText/RichTextRenderer'
import { useFeatureFlagEnabledWrapper } from 'bl-posthog/src/useFeatureFlagEnabledWrapper'

import { BookingBar } from 'flows/hotel-new/booking-bar/BookingBar'
import type { Room } from 'flows/hotel-new/booking-bar/urlParams'
import { urlParams } from 'flows/hotel-new/booking-bar/urlParams'
import { HotelEnumsHotelProperty } from 'gql/graphql'
import { formatGradient } from 'utils/formatters'

import { BannerHeightContext } from '../../../BannerHeight'
import * as styles from './styles'

// TODO - Remove dynamic importing
const SpaBookingBar = dynamic(() => import('../../../SpaBookingBar'), {
  ssr: false,
})

const HotelBookingBar = dynamic(() => import('../../../HotelBookingBar'), {
  ssr: false,
})

const documentOptions = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => (
      <Type
        as="h1"
        preset="headlineExtraLarge"
        bottom={{ xs: 0.5, mlg: 2 }}
        weight="bold"
      >
        {children}
      </Type>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Type preset="subtitle">{children}</Type>
    ),
  },
}

const serialize = createSerializer(urlParams)

type BookingBarSectionProps = {
  section: {
    fields: ISectionBookingBarHeroFields
  }
}

// Map of property union type values to their respective HotelEnumsHotelProperty
// This is used to ensure type safety when passing the property to the BookingBar component
const propertyEnumMap = {
  RETREAT: HotelEnumsHotelProperty.Retreat,
  SILICA: HotelEnumsHotelProperty.Silica,
} as const

export const BookingBarSection = ({
  section: {
    fields: {
      textContent,
      image,
      bookingBar,
      imageOverlay = 'light',
      video,
      config,
      backgroundPosition,
    },
  },
}: BookingBarSectionProps) => {
  const router = useRouter()
  const [videoError, setVideoError] = useState(false)
  const [offsetBottom, setOffsetBottom] = useState(0)
  const { bannerHeight } = useContext(BannerHeightContext)
  const { ctaLabel, gradient, property, settings, type } =
    bookingBar?.fields ?? {}
  const { availabilityMaxDate, defaultDaysStay: minDays } =
    settings?.fields ?? {}

  const formattedGradient = formatGradient(gradient)
  const maxDate = availabilityMaxDate
    ? new Date(availabilityMaxDate)
    : undefined

  useEffect(() => {
    if (bannerHeight !== offsetBottom) {
      setOffsetBottom(bannerHeight ?? 0)
    }
  }, [bannerHeight])

  const goToRoomSelectionScreen = (data: { rooms: Room[]; dates: Date[] }) => {
    const { rooms, dates } = data

    router.push({
      pathname: '/book/accommodation',
      search: serialize({
        rooms,
        dates: dates.map(d => fromZonedTime(d, 'UTC')),
        property: propertyEnumMap[property]
          ? propertyEnumMap[property].toLowerCase()
          : '',
      }),
    })
  }

  // TOOD: cleanup "new" from styles when removing the feature flag, and hardcode the gradient values, no need for it to use contentful
  const showNewBookingBar =
    useFeatureFlagEnabledWrapper('[HOT]booking-bar') &&
    (property === 'RETREAT' || property === 'SILICA')

  return (
    <styles.bookingBarHero bottom={config?.fields.bottom} offset={offsetBottom}>
      <styles.left>
        <styles.text>
          <RichTextRenderer
            document={textContent}
            customOptions={documentOptions}
          />
        </styles.text>
        {bookingBar ? (
          type === 'Hotel' ? (
            showNewBookingBar ? (
              <styles.bookingBarWrapNew>
                <styles.bookingBarNew
                  gradient={
                    property === 'RETREAT'
                      ? {
                          opacity: 0.3,
                          color1: '#D8E7E605',
                          color2: '#F5F6F5F7',
                          degrees: '312deg',
                        }
                      : formattedGradient
                  }
                >
                  <BookingBar
                    adultsOnly={property === 'RETREAT'}
                    maxDate={maxDate}
                    minDays={minDays}
                    onSubmit={goToRoomSelectionScreen}
                    property={propertyEnumMap[property]}
                    submitButtonLabel={ctaLabel}
                  />
                </styles.bookingBarNew>
              </styles.bookingBarWrapNew>
            ) : (
              <styles.bookingBarWrap>
                <styles.bookingBar
                  gradient={formattedGradient}
                  background={bookingBar.fields.backgroundColor}
                >
                  <HotelBookingBar
                    ctaLabel={ctaLabel}
                    property={property}
                    switchBetweenPanels
                    mobileBarAsButton
                    maxDate={availabilityMaxDate}
                  />
                </styles.bookingBar>
              </styles.bookingBarWrap>
            )
          ) : (
            <styles.bookingBarWrap>
              <styles.bookingBar
                gradient={formattedGradient}
                background={bookingBar.fields.backgroundColor}
              >
                <SpaBookingBar
                  ctaLabel={ctaLabel}
                  property={property}
                  mobileBarAsButton
                />
              </styles.bookingBar>
            </styles.bookingBarWrap>
          )
        ) : null}
      </styles.left>
      <styles.right aria-hidden imageOverlay={imageOverlay}>
        {video && !videoError ? (
          <video
            src={video.fields.file.url}
            autoPlay
            muted
            loop
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: backgroundPosition || 'center',
            }}
            onError={() => setVideoError(true)}
          />
        ) : (
          <ContentfulImage
            image={image}
            style={{ objectPosition: backgroundPosition }}
          />
        )}
      </styles.right>
    </styles.bookingBarHero>
  )
}

export default BookingBarSection
